import React, { useState } from "react";
import { Share2, Download, Copy, Check, MessageCircle } from "lucide-react";

/**
 * Share button cluster.
 * - Uses Web Share API (with image File) on supported devices
 * - Otherwise falls back to copy-link + WhatsApp + Download
 */
async function urlToFile(url, filename = "kingdom.png") {
  const res = await fetch(url, { mode: "cors" });
  const blob = await res.blob();
  return new File([blob], filename, { type: blob.type || "image/png" });
}

export default function ShareButtons({
  imageUrl,
  shareUrl,
  filename = "anagha-kingdom.png",
  title = "Anagha's Magical Kingdom",
  text = "I just became a character in Anagha's Magical Kingdom! ✨",
  compact = false,
}) {
  const [copied, setCopied] = useState(false);
  const [sharing, setSharing] = useState(false);

  const canShareFiles =
    typeof navigator !== "undefined" &&
    navigator.canShare &&
    navigator.canShare({ files: [new File([], "x.png", { type: "image/png" })] });

  const doShare = async () => {
    setSharing(true);
    try {
      if (canShareFiles && imageUrl) {
        const file = await urlToFile(imageUrl, filename);
        await navigator.share({ files: [file], title, text, url: shareUrl });
      } else if (navigator.share) {
        await navigator.share({ title, text, url: shareUrl });
      } else {
        // copy as fallback
        await navigator.clipboard.writeText(`${text} ${shareUrl || imageUrl}`);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch {
      // user cancelled or share failed
    }
    setSharing(false);
  };

  const doDownload = async () => {
    try {
      const file = await urlToFile(imageUrl, filename);
      const url = URL.createObjectURL(file);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      alert("Download failed: " + e.message);
    }
  };

  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl || imageUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(
    `${text} ${shareUrl || imageUrl}`
  )}`;

  if (compact) {
    return (
      <button
        type="button"
        onClick={doShare}
        disabled={sharing}
        className="glass rounded-full px-3 py-1.5 flex items-center gap-1 text-xs text-plum hover:bg-white/80 transition-colors"
        data-testid="share-compact-btn"
        title="Share"
      >
        <Share2 size={12} /> Share
      </button>
    );
  }

  return (
    <div className="flex flex-wrap gap-2 justify-center" data-testid="share-buttons">
      <button
        type="button"
        onClick={doShare}
        disabled={sharing}
        className="btn-royal py-2.5 px-5 text-sm"
        data-testid="share-btn"
      >
        <Share2 size={14} /> {sharing ? "Sharing…" : "Share"}
      </button>
      <button
        type="button"
        onClick={doDownload}
        className="btn-ghost-royal py-2.5 px-5 text-sm"
        data-testid="download-btn"
      >
        <Download size={14} /> Download
      </button>
      <a
        href={whatsappUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="btn-ghost-royal py-2.5 px-5 text-sm"
        data-testid="whatsapp-btn"
      >
        <MessageCircle size={14} /> WhatsApp
      </a>
      <button
        type="button"
        onClick={doCopy}
        className="btn-ghost-royal py-2.5 px-5 text-sm"
        data-testid="copy-link-btn"
      >
        {copied ? <Check size={14} /> : <Copy size={14} />}
        {copied ? "Copied!" : "Copy link"}
      </button>
    </div>
  );
}
